//
//  Deeds.tsx
//  TrajeXY Signet Server
//
//  Copyright © 2026 TrajeXY. All rights reserved.
//
//  Created: September 22, 2026
//  Author: Bob Keathley
//
//  Lists the deeds for one notebook, with a badge per deed kind.
//
import { useCallback, useEffect, useState } from 'react'
import { api } from '@shared/api/client'
import { isOk } from '@shared/api/envelope'
import type { Deed, Page } from '@shared/api/types'
import type { Session } from '@shared/auth'
import { href } from '@shared/router'

const when = (s: number) => new Date(s * 1000).toLocaleString()
const bdg = (d: string) => <span className={`badge b-${d}`}>{d.toUpperCase()}</span>

export default function Deeds({ session, notebookId }: { session: Session; notebookId: string }) {
  const [items, setItems] = useState<Deed[]>([])
  const [next, setNext] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const load = useCallback(async (cursor: number | null) => {
    setBusy(true)
    const q = cursor === null ? '' : `&cursor=${cursor}`
    const r = await api<Page<Deed>>(`/notebooks/${notebookId}/deeds?limit=50${q}`, {}, session.token)
    setBusy(false)
    if (!isOk(r)) { setError(r.error.message); return }
    setItems((prev) => (cursor === null ? r.data.items : [...prev, ...r.data.items]))
    setNext(r.data.next_cursor)
  }, [notebookId, session.token])
  useEffect(() => { setItems([]); load(null) }, [load])
  const redacted = items.some((d) => d.content_redacted)
  return (
    <>
      <p><a href={href('/fleet')}>‹ Fleet</a></p>
      <h2>Deeds <span className="hint mono">— notebook {notebookId.slice(0, 8)}…</span></h2>
      {redacted && <p className="hint">🔒 Your role sees deed metadata; the command text and reason are visible to admins (06 §4a).</p>}
      {error && <p className="err" role="alert">{error}</p>}
      <table>
        <thead><tr><th className="rt">Seq</th><th>When</th><th>Agent</th><th>Decision</th><th>Tool</th><th>Action</th><th>Reason</th><th>Phase</th></tr></thead>
        <tbody>
          {items.length === 0 && !busy && <tr><td colSpan={8} className="hint">No deeds yet.</td></tr>}
          {items.map((d) => (
            <tr key={d.id}>
              <td className="rt mono">{d.seq}</td>
              <td className="hint mono">{when(d.ts)}</td>
              <td className="mono">{d.agent}</td>
              <td>{bdg(d.decision)}{d.exception_id && <span className="exc-tag" title={`exception ${d.exception_id}`}>EXC</span>}</td>
              <td className="mono">{d.tool}</td>
              <td className="mono action">{d.content_redacted ? <span className="hint">🔒 redacted</span> : d.action}</td>
              <td className="hint">{d.content_redacted ? '—' : d.reason}</td>
              <td className="hint">{d.phase ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {next !== null && <p><button className="btn ghost" disabled={busy} onClick={() => load(next)}>{busy ? 'Loading…' : 'Load older'}</button></p>}
    </>
  )
}
